export function finiteNumberOrDefault(value, defaultValue) {
  const n = Number(value);
  return Number.isFinite(n) ? n : defaultValue;
}

export function normalizeMonthlyArray(arr, fallbackVal) {
  if (!Array.isArray(arr)) return new Array(12).fill(fallbackVal);
  const out = new Array(12).fill(fallbackVal);
  for (let i = 0; i < 12; i += 1) {
    const v = arr[i];
    out[i] = v ?? fallbackVal;
  }
  return out;
}

export function normalizeMonthlySolarAccess(arr) {
  const out = normalizeMonthlyArray(arr, 1);
  return out.map((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 1;
    return Math.min(1, Math.max(0, n));
  });
}

export function normalizeMonthlyLoss(arr) {
  const out = normalizeMonthlyArray(arr, 0);
  return out.map((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return n;
  });
}

function pickFirstDefined(obj, keys) {
  const src = obj && typeof obj === 'object' ? obj : null;
  if (!src) return undefined;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = src[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function lossValueOrDefault(raw, fallback) {
  if (raw === undefined || raw === null || raw === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function buildLossPayload(losses, defaults = {}) {
  const d = {
    Soiling: defaults.Soiling ?? 2,
    Shading: defaults.Shading ?? 0,
    Snow: defaults.Snow ?? 0,
    Mismatch: defaults.Mismatch ?? 1.5,
    Wiring: defaults.Wiring ?? 2.0,
    Connections: defaults.Connections ?? 0.5,
    Light_Induced_Degradation: defaults.Light_Induced_Degradation ?? 1.5,
    Nameplate_Rating: defaults.Nameplate_Rating ?? 1,
    Age: defaults.Age ?? 0,
    Availability: defaults.Availability ?? 1,
  };

  return {
    Soiling: lossValueOrDefault(
      pickFirstDefined(losses, ['soiling', 'Soiling']),
      d.Soiling
    ),
    Shading: lossValueOrDefault(
      pickFirstDefined(losses, ['shading', 'Shading']),
      d.Shading
    ),
    Snow: lossValueOrDefault(
      pickFirstDefined(losses, ['snow', 'Snow']),
      d.Snow
    ),
    Mismatch: lossValueOrDefault(
      pickFirstDefined(losses, ['mismatch', 'Mismatch']),
      d.Mismatch
    ),
    Wiring: lossValueOrDefault(
      pickFirstDefined(losses, ['wiring', 'Wiring']),
      d.Wiring
    ),
    Connections: lossValueOrDefault(
      pickFirstDefined(losses, ['connections', 'Connections']),
      d.Connections
    ),
    Light_Induced_Degradation: lossValueOrDefault(
      pickFirstDefined(losses, ['lid', 'Light_Induced_Degradation']),
      d.Light_Induced_Degradation
    ),
    Nameplate_Rating: lossValueOrDefault(
      pickFirstDefined(losses, [
        'nameplate_rating',
        'nameplateRating',
        'Nameplate_Rating',
      ]),
      d.Nameplate_Rating
    ),
    Age: lossValueOrDefault(
      pickFirstDefined(losses, ['age', 'Age']),
      d.Age
    ),
    Availability: lossValueOrDefault(
      pickFirstDefined(losses, ['availability', 'Availability']),
      d.Availability
    ),
  };
}

export function buildPanelsByIndexFromPanelShadingData({
  panelShadingData,
  segmentIndex,
}) {
  const panelsByIndex = {};
  if (!Array.isArray(panelShadingData) || panelShadingData.length === 0) {
    return panelsByIndex;
  }

  const monthlyData = panelShadingData[panelShadingData.length - 1];
  const isMonthlyData =
    monthlyData && typeof monthlyData === 'object' && !Array.isArray(monthlyData);
  const annualPanelData = isMonthlyData
    ? panelShadingData.slice(0, -1)
    : panelShadingData;

  const segmentPanels = annualPanelData.filter(
    (row) => Array.isArray(row) && row[0] === segmentIndex
  );

  segmentPanels.forEach((row) => {
    if (!Array.isArray(row) || row.length < 9) return;
    const panelIndex = row[2];
    if (panelsByIndex[panelIndex]) return;
    panelsByIndex[panelIndex] = {
      annual: {
        solar_access: row[6] ?? 1,
        tof: row[7] ?? 0,
        tsrf: row[8] ?? 0,
      },
      monthly: {},
    };
  });

  if (isMonthlyData && monthlyData) {
    for (let month = 1; month <= 12; month += 1) {
      const rows = monthlyData[month];
      if (!Array.isArray(rows)) continue;

      const monthSegmentPanels = rows.filter(
        (row) => Array.isArray(row) && row[0] === segmentIndex
      );

      const monthPanelsByIndex = {};
      monthSegmentPanels.forEach((row) => {
        if (!Array.isArray(row) || row.length < 9) return;
        const panelIndex = row[2];
        monthPanelsByIndex[panelIndex] = {
          solar_access: row[6] ?? 1,
          tof: row[7] ?? 0,
          tsrf: row[8] ?? 0,
        };
      });

      Object.keys(panelsByIndex).forEach((panelIndex) => {
        if (!panelsByIndex[panelIndex].monthly[month]) {
          panelsByIndex[panelIndex].monthly[month] = {
            solar_access: 1,
            tof: 0,
            tsrf: 0,
          };
        }
        if (monthPanelsByIndex[panelIndex]) {
          panelsByIndex[panelIndex].monthly[month] = monthPanelsByIndex[panelIndex];
        }
      });
    }
  }

  return panelsByIndex;
}

export function buildPerPanelShadingArrays({
  panelIndicesInPayload,
  panelCount,
  panelsByIndex,
  segmentMonthlySA,
  segmentMonthlyTOF,
  segmentMonthlyTSRF,
  segmentAnnualSA,
  segmentAnnualTOF,
  segmentAnnualTSRF,
}) {
  const monthly_solar_access = [];
  const monthly_tof = [];
  const monthly_tsrf = [];
  const annual_solar_access = [];
  const annual_tof = [];
  const annual_tsrf = [];

  const keys = panelsByIndex ? Object.keys(panelsByIndex) : [];
  const hasAnyPerPanel = keys.length > 0;

  if (!hasAnyPerPanel) {
    for (let i = 0; i < panelCount; i += 1) {
      monthly_solar_access.push([...segmentMonthlySA]);
      monthly_tof.push([...segmentMonthlyTOF]);
      monthly_tsrf.push([...segmentMonthlyTSRF]);
      annual_solar_access.push(segmentAnnualSA);
      annual_tof.push(segmentAnnualTOF);
      annual_tsrf.push(segmentAnnualTSRF);
    }
    return {
      monthly_solar_access,
      monthly_tof,
      monthly_tsrf,
      annual_solar_access,
      annual_tof,
      annual_tsrf,
    };
  }

  const temp = [];
  const allMonthlySAValues = [];

  for (let i = 0; i < panelCount; i += 1) {
    const payloadPanelIndex = panelIndicesInPayload[i];
    const perPanel = panelsByIndex[payloadPanelIndex];

    if (!perPanel) {
      temp.push(null);
      continue;
    }

    const msa = [];
    const mtof = [];
    const mtsrf = [];
    for (let m = 1; m <= 12; m += 1) {
      msa.push(perPanel.monthly[m]?.solar_access ?? 1);
      mtof.push(perPanel.monthly[m]?.tof ?? 0);
      mtsrf.push(perPanel.monthly[m]?.tsrf ?? 0);
    }

    const msaNorm = normalizeMonthlySolarAccess(msa);
    msaNorm.forEach((v) => {
      const n = Number(v);
      if (Number.isFinite(n)) allMonthlySAValues.push(n);
    });

    temp.push({
      msaNorm,
      mtof: mtof.map((v) => finiteNumberOrDefault(v, 0)),
      mtsrf: mtsrf.map((v) => finiteNumberOrDefault(v, 0)),
      annualSA: finiteNumberOrDefault(perPanel.annual?.solar_access, 1),
      annualTOF: finiteNumberOrDefault(perPanel.annual?.tof, 0),
      annualTSRF: finiteNumberOrDefault(perPanel.annual?.tsrf, 0),
    });
  }

  const maxMsa =
    allMonthlySAValues.length > 0 ? Math.max(...allMonthlySAValues) : null;
  const shouldFallbackAll = maxMsa !== null && maxMsa <= 0;

  for (let i = 0; i < panelCount; i += 1) {
    const v = shouldFallbackAll ? null : temp[i];
    if (!v) {
      monthly_solar_access.push([...segmentMonthlySA]);
      monthly_tof.push([...segmentMonthlyTOF]);
      monthly_tsrf.push([...segmentMonthlyTSRF]);
      annual_solar_access.push(segmentAnnualSA);
      annual_tof.push(segmentAnnualTOF);
      annual_tsrf.push(segmentAnnualTSRF);
    } else {
      monthly_solar_access.push(v.msaNorm);
      monthly_tof.push(v.mtof);
      monthly_tsrf.push(v.mtsrf);
      annual_solar_access.push(v.annualSA);
      annual_tof.push(v.annualTOF);
      annual_tsrf.push(v.annualTSRF);
    }
  }

  return {
    monthly_solar_access,
    monthly_tof,
    monthly_tsrf,
    annual_solar_access,
    annual_tof,
    annual_tsrf,
  };
}


